import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { PromptProvider } from './contexts/PromptContext';
import AssetProcessor from './components/AssetProcessor';
import EnhancedAssetProcessor from './components/EnhancedAssetProcessor';
import SearchPage from './pages/SearchPage';
import EnhancedPromptManagementPage from './pages/EnhancedPromptManagementPage';
import BulkIndexingManagementPage from './pages/BulkIndexingManagementPage';
import { ErrorBoundary } from './components/ErrorBoundary';

const AppContainer = styled.div`
    min-height: 100vh;
    background-color: #f8f9fa;
`;

const Header = styled.header`
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 16px 0;
    margin-bottom: 24px;
`;

const HeaderContent = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Title = styled.h1`
    margin: 0;
    color: #333;
    font-size: 1.5rem;
`;

const Nav = styled.nav`
    display: flex;
    gap: 16px;
`;

interface NavLinkProps {
    $active: boolean;
}

const NavLink = styled(Link)<NavLinkProps>`
    color: ${(props: NavLinkProps) => props.$active ? '#007bff' : '#666'};
    text-decoration: none;
    font-weight: ${(props: NavLinkProps) => props.$active ? '600' : '400'};
    padding: 8px 16px;
    border-radius: 4px;
    transition: all 0.2s;

    &:hover {
        background-color: ${(props: NavLinkProps) => props.$active ? 'rgba(0, 123, 255, 0.1)' : '#f5f5f5'};
    }
`;

const MainContent = styled.main`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 24px;
`;

const Navigation: React.FC = () => {
    const location = useLocation();

    return (
        <Nav>
            <NavLink 
                to="/search" 
                $active={location.pathname === '/search'}
            >
                Search Assets
            </NavLink>
            <NavLink 
                to="/process" 
                $active={location.pathname === '/process'}
            >
                Process Assets
            </NavLink>
            <NavLink 
                to="/enhanced-process" 
                $active={location.pathname === '/enhanced-process'}
            >
                Enhanced Processing
            </NavLink>
            <NavLink 
                to="/bulk-indexing" 
                $active={location.pathname === '/bulk-indexing'}
            >
                Bulk Indexing
            </NavLink>
            <NavLink 
                to="/prompt-management" 
                $active={location.pathname === '/prompt-management'}
            >
                Prompt Management
            </NavLink>
        </Nav>
    );
};

const App: React.FC = () => {
    return (
        <Router>
            <PromptProvider>
                <AppContainer>
                    <Header>
                        <HeaderContent>
                            <Title>Asset Management System</Title>
                            <Navigation />
                        </HeaderContent>
                    </Header>
                    <MainContent>
                        <Routes>
                            <Route path="/search" element={<SearchPage />} />
                            <Route path="/process" element={<AssetProcessor />} />
                            <Route path="/enhanced-process" element={<EnhancedAssetProcessor />} />
                            <Route
                                path="/bulk-indexing"
                                element={
                                    <ErrorBoundary>
                                        <BulkIndexingManagementPage />
                                    </ErrorBoundary>
                                }
                            />
                            <Route path="/prompt-management" element={<EnhancedPromptManagementPage />} />
                            <Route path="/" element={<SearchPage />} />
                        </Routes>
                    </MainContent>
                </AppContainer>
            </PromptProvider>
        </Router>
    );
};

export default App; 